import { Locale, LOCALE_INFO } from 'base/ui/locales';
import { NodeMeasurements } from 'blocks/component_renderer/node_measuring';
import { measureTextOffscreen } from 'blocks/component_renderer/text_overrides';
import { LLMProxyService } from 'blocks/llm_proxy_service';
import { makeObservable, observable } from 'mobx';
import { computedFn } from 'mobx-utils';
import { ElementInstance, Project, SectionRef, Space } from '../model';
import { LLMErrorFormatter } from './task_log';
import { fitTextToNumLines } from './text_fitting';

type RewritingTaskState = Readonly<
  | {
      type: 'pending';
      newText: string;
      apology?: undefined;
    }
  | {
      type: 'fulfilled';
      newText: string;
      apology?: string;
    }
  | {
      type: 'rejected';
      error: string;
    }
>;

export type TextRewritingLocation = {
  space: Space | 'home';
  // Intentionally a different property name to SelectionState.  Our consumers use a SelectionState
  // object which references a different location.  Stops them accidentally passing the wrong thing.
  sref: SectionRef;
  element: ElementInstance;
  nodeId: string;
};
export type Range = { start: number; end: number };

export function makeTextFittingPrompt(numLines: number) {
  return numLines === 1 ? '1 line' : `${numLines} lines`;
}
function maybeParseTextFittingPrompt(prompt: string): number | undefined {
  prompt = prompt.trim().toLowerCase();
  const match = prompt.match(/^(\d+)\s*lines?$/);
  if (match) {
    return parseInt(match[1], 10);
  }
}

export class RewritingTask {
  @observable.ref state: RewritingTaskState = {
    type: 'rejected',
    error: 'not started',
  };

  constructor(
    readonly containerMeasurements: NodeMeasurements,
    private readonly project: Project,
    readonly location: TextRewritingLocation,
    readonly originalText: string,
    readonly range: Range,
    readonly prompt: string,
    private readonly llm: LLMProxyService,
    private readonly errorFormatter: LLMErrorFormatter,
  ) {
    makeObservable(this);
    this.run();
  }

  get rerun() {
    return this.canRun ? this.run : undefined;
  }

  private get canRun() {
    return this.state == null || this.state.type !== 'pending';
  }

  private readonly run = async () => {
    if (!this.canRun) throw new Error('invalid state to run again');
    this.state = { type: 'pending', newText: '' };
    try {
      const numLines = maybeParseTextFittingPrompt(this.prompt);
      if (numLines) {
        await this.runTextFitting(numLines);
      } else {
        await this.runSimplePrompt();
      }
    } catch (error) {
      const msg = this.errorFormatter.process(error);
      this.state = { type: 'rejected', error: msg };
    }
  };

  private async runSimplePrompt() {
    const charStream = this.llm.infer('text-rewrite-1', {
      goal: this.prompt,
      text: this.originalText.slice(this.range.start, this.range.end),
      language: LOCALE_INFO[this.locale].name,
    });
    let chars = '';
    for await (const chunk of charStream) {
      chars += chunk;
      this.state = { type: 'pending', newText: chars };
    }
    this.state = { type: 'fulfilled', newText: chars.trim() };
  }

  private async runTextFitting(numLines: number) {
    const isFullRange =
      this.range.start <= 0 && this.range.end >= this.originalText.length - 1;
    if (!isFullRange) {
      throw new Error('Text fitting only works on full text');
    }

    const { text, apology } = await fitTextToNumLines(
      this.originalText,
      (text) =>
        measureTextOffscreen(this.containerMeasurements, text).lineCount,
      numLines,
      this.llm,
      this.locale,
      (text) => {
        this.state = { type: 'pending', newText: text };
      },
    );
    this.state = { type: 'fulfilled', newText: text, apology };
  }

  private get locale(): Locale {
    return (
      (this.location.space !== 'home' && this.location.space.localeOverride) ||
      this.project.locale ||
      'en-US'
    );
  }
}

export class TextRewritingAi {
  private readonly tasks = observable.map<string, readonly RewritingTask[]>(
    [],
    { deep: false },
  );

  constructor(
    private readonly project: Project,
    private readonly llm: LLMProxyService,
    private readonly errorFormatter: LLMErrorFormatter,
  ) {}

  start(
    containerMeasurements: NodeMeasurements,
    location: TextRewritingLocation,
    originalText: string,
    range: Range,
    prompt: string,
  ) {
    const newTask = new RewritingTask(
      containerMeasurements,
      this.project,
      location,
      originalText,
      range,
      prompt,
      this.llm,
      this.errorFormatter,
    );
    const key = idForLocation(location);
    this.tasks.set(key, [...(this.tasks.get(key) || []), newTask]);
    return newTask;
  }

  readonly getAll = computedFn(
    (location: TextRewritingLocation): readonly RewritingTask[] => {
      const key = idForLocation(location);
      return this.tasks.get(key) || [];
    },
  );

  isRewriting(location: TextRewritingLocation) {
    const id = idForLocation(location);
    return this.tasks.has(id);
  }

  removeAll(location: TextRewritingLocation) {
    this.tasks.delete(idForLocation(location));
  }
}

function idForLocation(location: TextRewritingLocation) {
  return `${location.sref.oid}/${location.element.oid}/${location.nodeId}`;
}
